import type { RowTarget, TableCell, TableRowModel, Tone } from './tableRows'

/** Where tapping a row (or a cell, on tablet) goes — same routes as ClickableCell. */
export function targetPath(target: RowTarget, offset: number) {
  switch (target.kind) {
    case 'vault':
      return `/vaults/${target.id}/transactions/new?offset=${offset}&type=transfer`
    case 'category':
      return `/categories/${target.id}/transactions/new?offset=${offset}&type=${target.type}`
    case 'add-vault':
      return '/vaults/new'
    case 'add-category':
      return `/categories/new?type=${target.type}`
  }
}

/** Is this row + period the one the open form is showing? */
export function isActiveTarget(
  target: RowTarget | undefined,
  offset: number,
  pathname: string,
  search: string,
) {
  if (!target || (target.kind !== 'vault' && target.kind !== 'category'))
    return false
  const base = target.kind === 'vault' ? 'vaults' : 'categories'
  const re = new RegExp(`/${base}/${target.id}/transactions(/|$)`)
  const params = new URLSearchParams(search)
  return re.test(pathname) && params.get('offset') === String(offset)
}

// Tints verbatim from DataTable.getCellColorClass.
const CAP_BG: Record<Tone, string> = {
  vault: 'bg-[#E4F1FF]',
  in: 'bg-green-lowest',
  out: 'bg-purple-lowest',
  neutral: 'bg-gray-lowest',
}
const GROUP_BG: Record<Tone, string> = {
  vault: 'bg-[#E4F1FF]',
  in: 'bg-[#E5F4E9]',
  out: 'bg-[#F5EEFA]',
  neutral: 'bg-white',
}
const LEAF_BG: Record<Tone, string> = {
  vault: 'bg-white',
  in: 'bg-[#EEF7F0]',
  out: 'bg-[#FAF5FE]',
  neutral: 'bg-white',
}

export function rowBackground(row: TableRowModel) {
  switch (row.kind) {
    case 'cap':
    case 'net':
      return CAP_BG[row.tone]
    case 'group':
      return GROUP_BG[row.tone]
    case 'closing':
      return 'bg-gray-low'
    default:
      return LEAF_BG[row.tone]
  }
}

export function amountClass(row: TableRowModel, cell: TableCell) {
  if (cell.amount === null || cell.amount === 0) return 'text-txt-placeholder'
  if (row.kind === 'net')
    return cell.amount < 0 ? 'text-txt-error' : 'text-txt-success'
  // Periods with actuals read in ink, pure estimates in secondary.
  return cell.offset <= 0 ? 'text-txt-primary' : 'text-txt-secondary'
}

/** Money Out over its estimate is bad; Money In over its estimate is good. */
export const discrepancyIsGood = (row: TableRowModel, d: number) =>
  row.isOut ? d < 0 : d > 0
